// DispatchDeck — Person identity-kit accessor (TASK-9359)
// ============================================================================
// `window.PersonIdentity` — the ONE way client code resolves and writes a
// person's birthdate and driver-licence identity now that they live in the
// sibling `tenants/{tid}/peopleIdentity/{personId}` instead of on the broadcast
// `people` doc.
//
// WHY. `firestore.rules` `people/{personId}` is `allow read: if canReadTenant`,
// and canReadTenant folds in hasDriverSession — so every driver-PIN phone was
// receiving every coworker's DOB + licence number / state / expiry + CDL number.
// No driver screen shows a coworker's. Firestore cannot field-redact a read, so
// the fields MOVE. Chris 2026-09-24 (binding): roster / HR / dispatch / admin
// keep seeing and editing them where they do today; partners get them
// server-side only. Access is MODULE access (owner via tenants.ownerId, members
// holding roster / hr / hr-directory / dispatch / compliance), never a role.
//
// WHAT MOVES (keep in lockstep with functions/src/services/personIdentity.js —
// tests/task9359-people-identity.unit.cjs runs both against one case table):
//   dob          canonical birthdate; the legacy `dateOfBirth` alias is merged
//                into it (dob wins).
//   dlNumber     licence number   (alias: license.number)
//   dlState      licence state    (alias: license.state)
//   dlExpiryDate licence expiry   (aliases: license.expires|expiry|expiryDate)
//   cdlNumber    CDL number. Every write also sets the non-sensitive boolean
//                `hasCdlNumber` on people, which is what the "is this a CDL
//                driver?" predicates read for sessions that can't see the vault.
//
// LOADING (personComp.js precedent — CLAUDE.md Pitfall #1): imported ONCE via a
// side-effect import from views/clock.js; every other module reads
// `window.PersonIdentity` at call time. It holds NO subscription until a view
// calls subscribe(), so a driver session that loads the bundle reads nothing.
//
// 🔴 DENIED ≠ ABSENT. A session the rules exclude gets permission-denied on the
// subscription. That is NOT "no DOB on file" — it is "you cannot know".
// `identityUnknown(person)` is the per-person predicate: surfaces render
// "Restricted" and edit forms must NOT save a blank over the real value (the
// TASK-1191 wipe vector). `splitForWrite` drops identity keys entirely on an
// UPDATE the caller cannot know, so a restricted editor's save never touches
// the vault.
//
// PARTNER SESSIONS ("Switch to DispatchDeck", window.isPartnerClientSession):
// partners have NO rules grant on the vault. They never see it here (reads are
// denied → "Restricted"), and a person they CREATE (approve-to-roster hire / add
// person) gets its identity written through the partner callable
// updateDriverCompliance after the people doc commits — server-side, audited.
//
// MERGE SEMANTICS (same as PersonComp): a key PRESENT on the vault doc
// (including an explicit null = "cleared") wins; an ABSENT key falls back to the
// person doc's legacy copy. Pre-migration every read is byte-identical.
//
// The pure core lives in `_personIdentityCoreFactory` (no Firebase, no DOM) so
// tests/task9359-people-identity.unit.cjs can run it in node:vm.

import {
  db,
  auth,
  collection,
  doc,
  setDoc,
  updateDoc,
  onSnapshot,
  writeBatch,
  Timestamp,
  functions,
  httpsCallable,
} from './firebase-config.js?v=1868';
import { deleteField } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';

// ── pure core (vm-testable, no Firebase) ────────────────────────────────────
function _personIdentityCoreFactory() {
  'use strict';

  const IDENTITY_FIELDS = ['dob', 'dlNumber', 'dlState', 'dlExpiryDate', 'cdlNumber'];
  // people `license` map sub-key -> canonical field it aliases.
  const LICENSE_ALIAS = { number: 'dlNumber', state: 'dlState', expires: 'dlExpiryDate', expiry: 'dlExpiryDate', expiryDate: 'dlExpiryDate' };
  const LICENSE_SUBKEYS = Object.keys(LICENSE_ALIAS);
  const CDL_MARKER = 'hasCdlNumber';

  // status: 'idle' | 'loading' | 'ready' | 'denied' | 'error' (PersonComp's).
  const state = { tenantId: null, status: 'idle', map: new Map() };

  function isBlank(v) {
    return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
  }
  function norm(v) { return isBlank(v) ? null : (typeof v === 'string' ? v.trim() : v); }
  function isPlainObject(v) { return v !== null && typeof v === 'object' && !Array.isArray(v); }
  function has(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }

  // The legacy copy a person doc itself still carries. An object that was
  // overlaid IN PLACE (overlayList) keeps its raw people-doc values in a
  // non-enumerable `__idRaw`, so re-overlaying after a vault update resolves
  // against the real legacy copy, not the previous overlay.
  function legacy(p0) {
    const out = {};
    if (!p0 || typeof p0 !== 'object') return out;
    const p = p0.__idRaw || p0;
    const lic = isPlainObject(p.license) ? p.license : {};
    const pick = (field, primary, aliases) => {
      const v = norm(primary);
      if (v !== null) { out[field] = v; return; }
      for (const a of aliases) { const n = norm(a); if (n !== null) { out[field] = n; return; } }
    };
    pick('dob', p.dob, [p.dateOfBirth]);
    pick('dlNumber', p.dlNumber, [lic.number]);
    pick('dlState', p.dlState, [lic.state]);
    pick('dlExpiryDate', p.dlExpiryDate, [lic.expires, lic.expiry, lic.expiryDate]);
    pick('cdlNumber', p.cdlNumber, []);
    return out;
  }

  function get(personId) { return (personId && state.map.get(personId)) || null; }

  // The one resolver every read site uses. Returns a COPY with canonical
  // identity fields filled in (vault wins per key, legacy fallback).
  function effective(person) {
    if (!person) return person;
    const vault = person.id ? state.map.get(person.id) : null;
    const leg = legacy(person);
    const merged = Object.assign({}, person);
    IDENTITY_FIELDS.forEach((k) => {
      if (vault && has(vault, k) && vault[k] !== undefined) merged[k] = vault[k];
      else if (leg[k] !== undefined) merged[k] = leg[k];
      else delete merged[k];
    });
    return merged;
  }

  // Single-field convenience: effective value or '' (never undefined/null).
  function value(person, field) {
    const e = effective(person);
    const v = e ? e[field] : null;
    return isBlank(v) ? '' : String(v);
  }

  // IN-PLACE overlay for views that hold a people array and read the fields at
  // dozens of sites (hr.js, roster.js, compliance.js, dispatch.js). Each object
  // gets the raw legacy values stashed non-enumerably (a spread / Object.keys /
  // JSON never carries the stash), then its canonical identity fields are set
  // from vault-over-legacy (or deleted when neither has one). Safe to call again
  // after every vault snapshot.
  function overlayList(list) {
    (list || []).forEach((p) => {
      if (!p || typeof p !== 'object') return;
      if (!has(p, '__idRaw')) {
        const raw = {
          dob: p.dob, dateOfBirth: p.dateOfBirth, dlNumber: p.dlNumber, dlState: p.dlState,
          dlExpiryDate: p.dlExpiryDate, cdlNumber: p.cdlNumber,
          license: isPlainObject(p.license) ? Object.assign({}, p.license) : p.license,
        };
        Object.defineProperty(p, '__idRaw', { value: raw, enumerable: false, configurable: true, writable: true });
      }
      const e = effective(p);
      IDENTITY_FIELDS.forEach((k) => { if (e[k] !== undefined) p[k] = e[k]; else delete p[k]; });
    });
    return list;
  }

  function status() { return state.status; }
  function isDenied() { return state.status === 'denied'; }
  // Denial AND a never-loaded failure both mean "cannot know".
  function cannotKnow() { return state.status === 'denied' || state.status === 'error'; }

  function personHasLegacyIdentity(p) { return Object.keys(legacy(p)).length > 0; }

  // 🔴 DENIED ≠ ABSENT per person: we cannot know the vault AND the person doc
  // carries no legacy copy to fall back on.
  // 'loading' counts too: an edit form opened before the vault snapshot lands
  // shows blanks, and if the snapshot then lands under it the blanks would read
  // as a deliberate clear (the in-place overlay updates `current`). Treating the
  // load window as "cannot know" makes that save drop the identity keys instead.
  // COD 2026-10-02 (Code Reviewer): during 'loading' a legacy copy is NOT
  // enough — a person can carry a legacy dob and a vault-only licence, and the
  // blank licence input would read as a clear once the snapshot lands. So the
  // whole kit is "cannot know" until the vault snapshot settles.
  function identityUnknown(person) {
    if (state.status === 'loading') return true;
    if (!cannotKnow()) return false;
    return !personHasLegacyIdentity(person);
  }

  // Split a people write.
  //   patch            what the caller was about to write to people/{pid}
  //   opts.isUpdate    true for update/merge writes: moved keys (and the
  //                    dateOfBirth alias) are deleteField()'d on people when a
  //                    legacy copy exists, so it cannot outlive the edit
  //   opts.current     the person as the form loaded it (to write only CHANGED
  //                    identity values — an unchanged save must not create a
  //                    vault doc or fire a spurious audit row)
  //   opts.deleteValue the delete sentinel (injected so the core stays pure)
  // Returns { personPatch, identityPatch } — identityPatch is {} when there is
  // nothing to write. On an UPDATE the caller CANNOT KNOW, identity keys are
  // dropped entirely (never a blank over a hidden value).
  function splitForWrite(patch, opts) {
    const o = opts || {};
    const personPatch = Object.assign({}, patch || {});
    const identityPatch = {};
    const hasVal = (k) => has(personPatch, k) && personPatch[k] !== undefined;
    const incoming = {};
    // license map aliases — strip the sub-keys, keep the rest of the map.
    const licAlias = {};
    if (isPlainObject(personPatch.license)) {
      const lic = Object.assign({}, personPatch.license);
      LICENSE_SUBKEYS.forEach((s) => {
        if (has(lic, s)) {
          const f = LICENSE_ALIAS[s];
          const n = norm(lic[s]);
          if (n !== null && licAlias[f] === undefined) licAlias[f] = n;
          delete lic[s];
        }
      });
      personPatch.license = lic;
    }
    if (hasVal('dob')) incoming.dob = norm(personPatch.dob);
    else if (hasVal('dateOfBirth')) incoming.dob = norm(personPatch.dateOfBirth);
    ['dlNumber', 'dlState', 'dlExpiryDate', 'cdlNumber'].forEach((f) => {
      if (hasVal(f)) incoming[f] = norm(personPatch[f]);
      else if (licAlias[f] !== undefined) incoming[f] = licAlias[f];
    });
    if (typeof incoming.dlState === 'string') incoming.dlState = incoming.dlState.toUpperCase();
    ['dob', 'dateOfBirth', 'dlNumber', 'dlState', 'dlExpiryDate', 'cdlNumber'].forEach((k) => { delete personPatch[k]; });

    // "Cannot know" only matters on an UPDATE, where a blank could overwrite a
    // hidden value. A CREATE has nothing hidden — store what was typed.
    const unknown = o.isUpdate ? (o.current ? identityUnknown(o.current) : cannotKnow()) : false;
    if (unknown) return { personPatch, identityPatch, dropped: Object.keys(incoming).some((k) => incoming[k] !== null) };

    const cur = o.current ? effective(o.current) : {};
    Object.keys(incoming).forEach((k) => {
      const before = norm(cur && cur[k]);
      if (!o.current || String(before === null ? '' : before) !== String(incoming[k] === null ? '' : incoming[k])) {
        identityPatch[k] = incoming[k];
      }
    });
    // A create never needs to store "nothing".
    if (!o.isUpdate) Object.keys(identityPatch).forEach((k) => { if (identityPatch[k] === null) delete identityPatch[k]; });

    if (has(identityPatch, 'cdlNumber')) personPatch[CDL_MARKER] = identityPatch.cdlNumber !== null;
    if (o.isUpdate && has(o, 'deleteValue')) {
      // Strip the legacy copy ONLY for a field this write is putting in the
      // vault (so the value is never absent from both places), and only when a
      // legacy key actually exists (so a routine save of a migrated person
      // sends no deletes). An UNCHANGED field keeps its legacy copy until the
      // migration moves it — deleting it here would lose the value.
      const leg = (o.current && o.current.__idRaw) || o.current || {};
      const onDoc = (k) => leg[k] !== undefined;
      Object.keys(identityPatch).forEach((f) => { if (onDoc(f)) personPatch[f] = o.deleteValue; });
      if (has(identityPatch, 'dob') && onDoc('dateOfBirth')) personPatch.dateOfBirth = o.deleteValue;
      // A license map on the stored doc that this write does NOT replace keeps
      // its alias sub-keys until the migration removes them — UNLESS this write
      // moves the canonical field, in which case the alias goes too (a stale
      // alias would otherwise resurrect through legacy()).
      if (isPlainObject(leg.license) && !has(patch || {}, 'license')) {
        LICENSE_SUBKEYS.forEach((s) => {
          if (has(leg.license, s) && has(identityPatch, LICENSE_ALIAS[s])) personPatch['license.' + s] = o.deleteValue;
        });
      }
    }
    return { personPatch, identityPatch, dropped: false };
  }

  // The vault doc payload (only identity fields + bookkeeping). null = nothing.
  function vaultPayload(personId, identityPatch, uid, now) {
    const payload = {};
    IDENTITY_FIELDS.forEach((k) => {
      if (identityPatch && has(identityPatch, k) && identityPatch[k] !== undefined) payload[k] = identityPatch[k];
    });
    if (!Object.keys(payload).length) return null;
    return Object.assign(payload, { personId, updatedAt: now, updatedBy: uid || null });
  }

  // Partner callable payload: updateDriverCompliance treats '' as "clear".
  function partnerPayload(identityPatch) {
    const out = {};
    IDENTITY_FIELDS.forEach((k) => {
      if (identityPatch && has(identityPatch, k) && identityPatch[k] !== undefined) {
        out[k] = identityPatch[k] === null ? '' : String(identityPatch[k]);
      }
    });
    return out;
  }

  function _applySnapshot(docs) {
    state.map = new Map();
    (docs || []).forEach((d) => { if (d && d.id) state.map.set(d.id, d.data || d); });
    state.status = 'ready';
  }
  function _applyError(code) {
    if (code === 'permission-denied') { state.status = 'denied'; return; }
    if (state.status !== 'ready') state.status = 'error';
  }
  function _applyLocalWrite(personId, patch) {
    if (!personId) return;
    const prev = state.map.get(personId) || {};
    state.map.set(personId, Object.assign({}, prev, patch));
  }
  function _reset(tenantId) {
    state.tenantId = tenantId || null;
    state.status = tenantId ? 'loading' : 'idle';
    state.map = new Map();
  }

  return {
    IDENTITY_FIELDS, LICENSE_SUBKEYS, CDL_MARKER, isBlank, legacy, get, effective, value, overlayList, status,
    isDenied, cannotKnow, personHasLegacyIdentity, identityUnknown, splitForWrite, vaultPayload, partnerPayload,
    _applySnapshot, _applyError, _applyLocalWrite, _reset, _state: state,
  };
}

// ── Firebase wiring ─────────────────────────────────────────────────────────
const _core = _personIdentityCoreFactory();
let _unsubscribe = null;
const _listeners = new Map();

function _notify() {
  _listeners.forEach((cb) => {
    try { cb(); } catch (e) { console.warn('[PersonIdentity] listener failed:', e && e.message); }
  });
}

function _isPartner() {
  try { return !!(window.isPartnerClientSession && window.isPartnerClientSession()); } catch (_) { return false; }
}
function _uid() { return (auth && auth.currentUser && auth.currentUser.uid) || null; }

// Idempotent per tenant (PersonComp.subscribe shape). Views that show or edit
// identity fields (roster, hr, compliance, dispatch) call this from their own
// people-subscription path. A denied subscription is EXPECTED for sessions the
// rules exclude — recorded, not shouted about.
function subscribe(tenantId, opts) {
  const key = (opts && opts.key) || 'default';
  if (opts && typeof opts.onUpdate === 'function') _listeners.set(key, opts.onUpdate);
  if (!tenantId) return;
  if (_core._state.tenantId === tenantId && _unsubscribe) return;
  if (_unsubscribe) { try { _unsubscribe(); } catch (e) { /* already dead */ } _unsubscribe = null; }
  _core._reset(tenantId);
  // A partner session has no rules grant — don't even ask (a guaranteed denial).
  if (_isPartner()) { _core._applyError('permission-denied'); _notify(); return; }
  _unsubscribe = onSnapshot(
    collection(db, 'tenants', tenantId, 'peopleIdentity'),
    (snap) => {
      _core._applySnapshot(snap.docs.map((d) => ({ id: d.id, data: d.data() })));
      _notify();
    },
    (err) => {
      const code = err && err.code;
      _core._applyError(code);
      if (code !== 'permission-denied') {
        console.warn('[PersonIdentity] peopleIdentity subscription error:', (err && err.message) || err);
      }
      _notify();
    }
  );
}

function splitForWrite(patch, opts) {
  return _core.splitForWrite(patch, Object.assign({}, opts || {}, { deleteValue: deleteField() }));
}

function vaultRef(tenantId, personId) {
  return doc(db, 'tenants', tenantId, 'peopleIdentity', personId);
}
function vaultPayload(personId, identityPatch) {
  return _core.vaultPayload(personId, identityPatch, _uid(), Timestamp.now());
}

function _toastDropped() {
  try {
    window.showToast?.("Birthdate / license changes were not saved — your login can't edit them.", 'warning');
  } catch (_) { /* no toast host */ }
}

// Write identity fields for a person that ALREADY EXISTS (merge). Partner
// sessions go through the server (updateDriverCompliance); everyone else writes
// the vault directly. Returns { ok, code } — callers must SURFACE a failure (a
// stale vault doc WINS over the person doc).
async function writeIdentity(tenantId, personId, identityPatch) {
  if (!tenantId || !personId) return { ok: false, code: 'bad-args' };
  const vp = vaultPayload(personId, identityPatch);
  if (!vp) return { ok: true, code: null };
  try {
    if (_isPartner()) {
      const fn = httpsCallable(functions, 'updateDriverCompliance');
      await fn({ tenantId, personId, updates: _core.partnerPayload(identityPatch) });
    } else {
      await setDoc(vaultRef(tenantId, personId), vp, { merge: true });
    }
    if (_core._state.tenantId === tenantId) _core._applyLocalWrite(personId, identityPatch);
    _notify();
    return { ok: true, code: null };
  } catch (err) {
    console.warn('[PersonIdentity] identity write failed for', personId, (err && err.message) || err);
    return { ok: false, code: (err && err.code) || 'unknown' };
  }
}

// CREATE a person inside a batch the CALLER owns (e.g. the hire batch). Strips
// the identity kit off `data`, puts it in the same batch when this session is
// known to be able to write the vault, otherwise DEFERS it (partner session, a
// session whose vault read was denied, or a view that never subscribed) so the
// people create can never be failed by the vault leg. Call
// `flushDeferred(tenantId, personRef.id, deferred)` after `batch.commit()`.
function addToCreateBatch(batch, tenantId, personRef, data) {
  const { personPatch, identityPatch } = splitForWrite(data, { isUpdate: false });
  batch.set(personRef, personPatch);
  const vp = vaultPayload(personRef.id, identityPatch);
  if (!vp) return { deferred: null };
  if (!_isPartner() && _core.status() === 'ready') {
    batch.set(vaultRef(tenantId, personRef.id), vp, { merge: true });
    return { deferred: null, inBatch: identityPatch };
  }
  return { deferred: identityPatch };
}
async function flushDeferred(tenantId, personId, deferred, result) {
  if (result && result.inBatch && _core._state.tenantId === tenantId) {
    _core._applyLocalWrite(personId, result.inBatch); _notify();
  }
  if (!deferred || !Object.keys(deferred).length) return { ok: true, code: null };
  const r = await writeIdentity(tenantId, personId, deferred);
  if (!r.ok) {
    try { window.showToast?.('Saved — but the birthdate / license details could not be stored. Re-enter them from the person record.', 'warning'); } catch (_) { /* */ }
  }
  return r;
}

// UPDATE a person, routing identity fields to the vault. When the vault is
// known-writable they ride the SAME atomic batch (both land or neither does —
// so a legacy-copy delete can never land without the vault write that replaces
// it). `current` is the person as the form loaded it.
async function updatePerson(tenantId, personId, patch, current) {
  const split = splitForWrite(patch, { isUpdate: true, current: current || { id: personId } });
  const { personPatch, identityPatch } = split;
  if (split.dropped) _toastDropped();
  const hasIdentity = Object.keys(identityPatch).length > 0;
  const personRef = doc(db, 'tenants', tenantId, 'people', personId);
  if (!hasIdentity) {
    if (Object.keys(personPatch).length) await updateDoc(personRef, personPatch);
    return { personPatch, identityPatch };
  }
  // The people patch minus the legacy-copy deletes, for every path where the
  // vault write is NOT in the same batch — so a refused / failed vault write can
  // never strand a value nowhere.
  const safePatch = {};
  Object.keys(personPatch).forEach((k) => {
    if (_core.IDENTITY_FIELDS.includes(k) || k === 'dateOfBirth' || k.startsWith('license.')) return;
    safePatch[k] = personPatch[k];
  });
  if (!_isPartner() && _core.cannotKnow()) {
    // This session's vault read was refused, so a vault write would be too —
    // and in a batch it would take the whole people save down with it. Save the
    // rest, say plainly that the identity edit did not land.
    if (Object.keys(safePatch).length) await updateDoc(personRef, safePatch);
    _toastDropped();
    return { personPatch: safePatch, identityPatch: {} };
  }
  if (_isPartner()) {
    // No vault grant: people first, then the server writes the vault + strips
    // the legacy copy itself (updateDriverCompliance, Admin SDK).
    if (Object.keys(safePatch).length) await updateDoc(personRef, safePatch);
    const r = await writeIdentity(tenantId, personId, identityPatch);
    if (!r.ok) throw Object.assign(new Error('Birthdate / license details could not be saved.'), { code: r.code });
    return { personPatch: safePatch, identityPatch };
  }
  if (_core.status() !== 'ready') {
    // Vault write-ability not yet proven (no settled snapshot): never let the
    // vault leg take the people save down with it (COD Edge Case). People
    // first — without the legacy deletes — then the vault; a refused vault
    // write is reported, and the legacy copy is still there for the migration.
    if (Object.keys(safePatch).length) await updateDoc(personRef, safePatch);
    const r = await writeIdentity(tenantId, personId, identityPatch);
    if (!r.ok) _toastDropped();
    return { personPatch: safePatch, identityPatch: r.ok ? identityPatch : {} };
  }
  const batch = writeBatch(db);
  if (Object.keys(personPatch).length) batch.update(personRef, personPatch);
  batch.set(vaultRef(tenantId, personId), vaultPayload(personId, identityPatch), { merge: true });
  await batch.commit();
  if (_core._state.tenantId === tenantId) _core._applyLocalWrite(personId, identityPatch);
  _notify();
  return { personPatch, identityPatch };
}

window.PersonIdentity = {
  IDENTITY_FIELDS: _core.IDENTITY_FIELDS,
  CDL_MARKER: _core.CDL_MARKER,
  get: _core.get,
  effective: _core.effective,
  value: _core.value,
  overlayList: _core.overlayList,
  status: _core.status,
  isDenied: _core.isDenied,
  cannotKnow: _core.cannotKnow,
  identityUnknown: _core.identityUnknown,
  personHasLegacyIdentity: _core.personHasLegacyIdentity,
  splitForWrite,
  subscribe,
  writeIdentity,
  vaultRef,
  vaultPayload,
  addToCreateBatch,
  flushDeferred,
  updatePerson,
};
